/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
    Box,
    Button,
    Divider,
    IconButton,
    InputBase,
    MenuItem,
    Paper,
    Stack,
    Typography,
    Select,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CtaButton from "../../buttonWithArrow";
import PickUpLocation from '../../goole-location/pickupLocation';
import { useRouter } from "next/navigation";
import SmartDateField from "../../DatePickers/SmartDateField";

// ---------- helpers ----------
const pad = (n) => String(n).padStart(2, "0");
const toLocalInputValue = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const addMinutes = (d, mins) => new Date(d.getTime() + mins * 60 * 1000);
const roundToStep = (d, step = 15) => {
    const m = d.getMinutes();
    const rounded = Math.ceil(m / step) * step;
    d.setMinutes(rounded, 0, 0);
    return d;
};

// recomputeable getter for min (now + 4h, snapped to step)
const STEP_MIN = 15;
// const getMinDateTime = () => toLocalInputValue(roundToStep(addMinutes(new Date(), 240), STEP_MIN));
const getMinDateTime = () => toLocalInputValue(addMinutes(new Date(), 240));

export default function CarBookRide({ duration, hourly, setHourly, drop, setDrop, pickup, setPickup, dateTime, setDateTime, handleSearchIcon }) {
    const router = useRouter();
    const autocompleteFirst = useRef()
    const [loading, setLoading] = useState(true)
    const [isSubmitClicked, setIsSubmitClicked] = useState(false);
    const [focusNext, setFocusNext] = useState(false)
    const [timeOpt, setTimeOpt] = useState("now");
    const [minDateTime, setMinDateTime] = useState(getMinDateTime());

    // (optional) keep min moving forward if the page stays open
    useEffect(() => {
        const id = setInterval(() => setMinDateTime(getMinDateTime()), 60_000); // update every minute
        return () => clearInterval(id);
    }, []);

    const isBelowMin = useMemo(
        () => new Date(dateTime) < new Date(minDateTime),
        [dateTime, minDateTime]
    );

    // clamp any user-provided value to >= min
    const clampToMin = (isoLocal) => {
        const picked = new Date(isoLocal);
        const min = new Date(minDateTime);
        if (Number.isNaN(picked.getTime())) return minDateTime;
        return picked < min ? minDateTime : toLocalInputValue(picked);
    };

    return (
        <Paper
            elevation={6}
            sx={{
                maxWidth: 1310,
                mx: "auto",
                p: 3,
                borderRadius: "20px",
                bgcolor: "#fff",
            }}
        >
            {/* Header */}
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 22, mb: 1 }}>
                Book Your Ride
            </Typography>

            <Divider orientation="horizontal" flexItem sx={{ borderColor: "#e6eaee", mx: 0.5, mt: 1, mb: 3, opacity: 1 }} />

            {/* Row */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems="end"
            >
                {/* Pickup */}

                <FieldCard label="Pickup Location" hourly={hourly}>
                    <div style={{ width: '100%' }}>
                        <PickUpLocation
                            type={'pickup'}
                            pickup={pickup}
                            newDesign
                            size='small'
                            label='Pickup Location'
                            placeholder='Search a location'
                            setLoading={setLoading}
                            loading={loading}
                            autocompleteFirst={autocompleteFirst}
                            setPickup={mPickUp => {
                                setPickup(mPickUp)
                            }}
                            setDrop={mDrop => {
                                setDrop(mDrop)
                            }}
                            error={
                                (isSubmitClicked && pickup === '') || pickup === null
                                    ? true
                                    : false
                            }
                            helperText={
                                (isSubmitClicked && pickup === '') || pickup === null
                                    ? 'Pickup Location Required'
                                    : ''
                            }
                        />
                    </div>
                </FieldCard>

                {/* Drop */}
                <FieldCard label="Drop Location" hourly={hourly}>
                    <div style={{ width: '100%' }}>
                        <PickUpLocation
                            type={'drop'}
                            size='small'
                            drop={drop}
                            newDesign
                            setPickup={mPickUp => {
                                setPickup(mPickUp)
                            }}
                            label='Drop Location'
                            placeholder='Search a location'
                            setLoading={setLoading}
                            loading={loading}
                            focusNext={focusNext}
                            setDrop={mDrop => {
                                setDrop(mDrop)
                            }}
                            error={isSubmitClicked && drop === '' ? true : false}
                            helperText={
                                isSubmitClicked && drop === '' ? 'Drop Location Required' : ''
                            }
                        />
                    </div>
                </FieldCard>

                {Number(hourly) > 0 && (
                    <FieldCard label="Hours" hourly={hourly}>
                        <Box sx={{ width: "100%" }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                                <Select
                                    fullWidth
                                    size="small"
                                    value={hourly}
                                    onChange={(e) => setHourly(Number(e.target.value))}
                                    variant="standard"
                                    disableUnderline
                                    IconComponent={ExpandMoreIcon}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: 14,
                                        px: 1,
                                        ".MuiSelect-select": { pr: 3, py: 0.5 },
                                    }}
                                    MenuProps={{
                                        PaperProps: { sx: { maxHeight: 300 } },
                                    }}
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                                        const durationInHours = duration ? Math.ceil(duration / 60) : 0;
                                        const shouldDisable = duration < 60 ? false : h <= durationInHours;
                                        return (
                                            <MenuItem key={h} value={h} disabled={shouldDisable}>
                                                {h} {h === 1 ? "hour" : "hours"}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </Stack>
                        </Box>
                    </FieldCard>
                )}

                <FieldCard label="Pickup Date & Time" hourly={hourly}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                        <CalendarMonthOutlinedIcon fontSize="small" />

                        <SmartDateField
                            value={dateTime}
                            setValue={setDateTime}
                            minDateTime={minDateTime}   // ← use T
                        />

                        {/* Native datetime-local keeps zero deps and matches your styling */}
                        {/* <InputBase
                            type="datetime-local"
                            value={dateTime}
                            onChange={(e) => setDateTime(clampToMin(e.target.value))}
                            inputProps={{
                                min: minDateTime,        // hard limit in the native picker UI
                                step: 900,               // 15-minute increments (optional)
                                onInvalid: (e) => {
                                    e.target.setCustomValidity(
                                        `Pick a time ≥ ${minDateTime.replace('T', ' ')}`
                                    );
                                },
                                onInput: (e) => {
                                    // clear the message once it's valid/changed
                                    e.target.setCustomValidity('');
                                },
                            }}
                            sx={{ fontSize: 14, pr: 1, width: "100%" }}
                        /> */}
                    </Stack>
                </FieldCard>

                {/* CTA */}
                {/* <CtaButton cta={{ label: "Book Now", href: "#" }} /> */}
                <CtaButton label="Book Now" onClick={() => handleSearchIcon()} />
            </Stack>
        </Paper>
    );
}

/** Small helper: field container with label (matches your pill style) */
function FieldCard({ label, children, hourly }) {
    return (
        <Box sx={{ flex: 1, minWidth: { xs: "100%", md: hourly ? 150 : 260 } }}>
            <Typography
                component="label"
                sx={{
                    fontWeight: 600,
                    fontSize: "17px",
                    lineHeight: "100%",
                    letterSpacing: "0",
                    color: "#111",
                    mb: 0.75,
                    display: "block",
                }}
            >
                {label}
            </Typography>

            <Paper
                variant="outlined"
                sx={{
                    height: 56,
                    px: 1.25,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "10px",
                    borderColor: "#e6eaee",
                    bgcolor: "#f5f7f8",
                }}
            >
                {children}
            </Paper>
        </Box>
    );
}

/** Icon chip at field left */
function IconChip({ children }) {
    return (
        <IconButton
            size="small"
            disableRipple
            sx={{
                p: 0,
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "#fff",
                border: "1px solid #e6eaee",
                display: "grid",
                placeItems: "center",
            }}
        >
            {children}
        </IconButton>
    );
}
